import { type SetStoreFunction } from 'solid-js/store';
import type { Poster } from './posterSchema';
import { createCanvasState, type CanvasContext } from '../canvas/canvas';
import { createSelectionState, type SelectionContext } from '../canvas/selection';
import { createFontState, type FontContext } from '../fonts/fonts';
import { createObjectState, type ObjectContext } from './objects';
import { createDragState, type DragContext } from '../canvas/drag';
import { createHistoryState, type HistoryContext } from './history';
import { createImageState, type ImageContext } from '../canvas/images/images';
import { createDesignLinkState, type DesignLinkContext } from './designLink';
import type { Dimensions } from 'Shared/posters/types';

export interface IPosterBuilder {
	canvas: CanvasContext;
	selection: SelectionContext;
	fonts: FontContext;
	objects: ObjectContext;
	drag: DragContext;
	history: HistoryContext;
	images: ImageContext;
	// User-mode link to the original nested template design (inert when none is supplied).
	design: DesignLinkContext;
}

//TODO split into createReadOnlyBuilder, createUserBuilder, createDesignBuilder

export function createPosterBuilder(
	layout: () => Poster,
	setLayout: SetStoreFunction<Poster>|undefined,
	/* Only used in user mode - the nested template design ("shadow") that our flat layout is linked to.
	   Inert until it arrives, so an accessor that is briefly undefined (a resource, or a snapshot taken
	   on entering user mode) is fine. */
	linkedDesign: (() => Poster|undefined)|undefined,
	/* The fonts of the poster's theme: the whole set a design may use, on top of the built-ins. */
	themeFonts: () => string[],
	dims: () => Dimensions,
	proxyProductionAssets: boolean,
): IPosterBuilder
{
	/* Each sub-state takes the absence of a setter as "read-only" and keeps its own private copy.
	   The slice wrappers therefore have to be absent as well when there is no setLayout: an
	   always-truthy wrapper closing over an undefined setLayout made every read-only builder look
	   editable, and it threw on the first write (seeding a design that still needed ids). */
	const write = setLayout;

	const selection = createSelectionState();
	const canvas = createCanvasState(() => layout().canvas,
		write ? (...args) => write('canvas',...args) : undefined, dims);
	const fonts = createFontState(themeFonts);

	const objects = createObjectState(() => layout().objects,
		write ? (...args) => write('objects',...args) : undefined);
	const drag = createDragState();
	const history = createHistoryState();
	const images = createImageState(proxyProductionAssets);

	// The user-mode link to the nested template design. Inert (linked() === false) whenever no
	// linkedDesign is supplied — i.e. in design/read mode. Linking is by shared leaf id; a version
	// gate belongs here once the schema carries a version (TODO: skip linking when versions differ).
	const design = createDesignLinkState(linkedDesign, objects.objects, selection.impls, dims);

	return { canvas, selection, fonts, objects, drag, history, images, design };
}

